import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { safeNext } from './safeNext';
import { ArrowRight } from 'lucide-react';
import SubpageShell from '../pages/SubpageShell';
import SEO from '../components/SEO';
import { useMember, formatApiError } from './MemberAuthContext';
import '../styles/Access.css';
import '../styles/AccessAuth.css';

const MemberJoin = () => {
  const { register } = useMember();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const prefill = useLocation().state?.prefill || {};
  const [f, setF] = useState({ first_name: prefill.first_name || '', last_name: prefill.last_name || '', email: prefill.email || '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) {
      setError('Please choose a password of at least 8 characters.'); return;
    }
    setBusy(true); setError('');
    try {
      await register({
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim() || undefined,
        email: f.email.trim().toLowerCase(),
        password: f.password,
      });
      navigate(next || '/access/account', { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, 'Could not create your account. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SubpageShell currentPath="/access/join">
      <SEO title="Join Free — N3URO Access" description="Create your free N3URO Access account." path="/access/join" noindex />
      <section className="access-auth-band">
        <div className="access-auth-card" data-testid="member-join-card">
          <h1 className="access-auth-title">Join <span className="access-brand-rainbow">N3URO ACCESS</span> — Free</h1>
          <p className="access-auth-lede">Create your Free Access account. Upgrade any time.</p>
          <form onSubmit={submit} className="access-signup-form" noValidate>
            <div className="access-form-row">
              <input className="access-input" placeholder="First name" required value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="join-first" autoComplete="given-name" />
              <input className="access-input" placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="join-last" autoComplete="family-name" />
            </div>
            <input className="access-input" type="email" placeholder="Email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="join-email" autoComplete="email" />
            <input className="access-input" type="password" placeholder="Password (min 8 characters)" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="join-password" autoComplete="new-password" />
            {error && <p className="access-auth-error" data-testid="join-error">{error}</p>}
            <button type="submit" className="access-signup-cta" disabled={busy} data-testid="join-submit">
              {busy ? 'CREATING…' : (<>JOIN FREE ACCESS <ArrowRight size={16} /></>)}
            </button>
            <p className="access-signup-fineprint">Free for parents and carers. No card required.</p>
          </form>
          <div className="access-auth-links">
            <span>Already have an account?</span> <Link to={next ? `/access/sign-in?next=${encodeURIComponent(next)}` : '/access/sign-in'} data-testid="join-signin">Sign in</Link>
          </div>
        </div>
      </section>
    </SubpageShell>
  );
};

export default MemberJoin;
